'use client'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { usePublicRegions } from '@/hooks/use-esim'
import Flag from '@/components/ui/flag'

// Must stay identical to toEsimSlug in lib/esim/destinations.ts, which the
// route and sitemap share. Duplicated rather than imported because this is a
// client component and that module pulls in serverFetch.
function toEsimSlug(name: string) {
  return name.toLowerCase().replace(/\s+/g, '-')
}

type LinkItem = { label: string; href: string; iso2?: string }

// Mirrors the static routes under app/(marketing)/money-transfer/send-money-to-*
const SEND_MONEY_CORRIDORS: LinkItem[] = [
  { label: 'AUD to BRL', href: '/money-transfer/send-money-to-brazil', iso2: 'br' },
  { label: 'AUD to CAD', href: '/money-transfer/send-money-to-canada', iso2: 'ca' },
  { label: 'AUD to CLP', href: '/money-transfer/send-money-to-chile', iso2: 'cl' },
  { label: 'AUD to CNY', href: '/money-transfer/send-money-to-china', iso2: 'cn' },
  { label: 'AUD to CZK', href: '/money-transfer/send-money-to-czech-republic', iso2: 'cz' },
  { label: 'AUD to DKK', href: '/money-transfer/send-money-to-denmark', iso2: 'dk' },
  { label: 'AUD to HKD', href: '/money-transfer/send-money-to-hong-kong', iso2: 'hk' },
  { label: 'AUD to INR', href: '/money-transfer/send-money-to-india', iso2: 'in' },
  { label: 'AUD to IDR', href: '/money-transfer/send-money-to-indonesia', iso2: 'id' },
  { label: 'AUD to MYR', href: '/money-transfer/send-money-to-malaysia', iso2: 'my' },
  { label: 'AUD to MXN', href: '/money-transfer/send-money-to-mexico', iso2: 'mx' },
  { label: 'AUD to MAD', href: '/money-transfer/send-money-to-morocco', iso2: 'ma' },
  { label: 'AUD to NPR', href: '/money-transfer/send-money-to-nepal', iso2: 'np' },
  { label: 'AUD to NZD', href: '/money-transfer/send-money-to-new-zealand', iso2: 'nz' },
  { label: 'AUD to NOK', href: '/money-transfer/send-money-to-norway', iso2: 'no' },
  { label: 'AUD to PHP', href: '/money-transfer/send-money-to-philippines', iso2: 'ph' },
  { label: 'AUD to PLN', href: '/money-transfer/send-money-to-poland', iso2: 'pl' },
  { label: 'AUD to SGD', href: '/money-transfer/send-money-to-singapore', iso2: 'sg' },
  { label: 'AUD to ZAR', href: '/money-transfer/send-money-to-south-africa', iso2: 'za' },
  { label: 'AUD to LKR', href: '/money-transfer/send-money-to-sri-lanka', iso2: 'lk' },
  { label: 'AUD to SEK', href: '/money-transfer/send-money-to-sweden', iso2: 'se' },
  { label: 'AUD to THB', href: '/money-transfer/send-money-to-thailand', iso2: 'th' },
  { label: 'AUD to AED', href: '/money-transfer/send-money-to-uae', iso2: 'ae' },
  { label: 'AUD to GBP', href: '/money-transfer/send-money-to-uk', iso2: 'gb' },
  { label: 'AUD to USD', href: '/money-transfer/send-money-to-usa', iso2: 'us' },
  { label: 'AUD to VND', href: '/money-transfer/send-money-to-vietnam', iso2: 'vn' },
]

function SitemapLink({ label, href, iso2 }: LinkItem) {
  return (
    <Link
      href={href}
      className="group flex items-center gap-2 global-border-radius px-2.5 py-2 text-sm text-brand-bg/65 transition-colors hover:bg-black/[0.04] hover:text-brand-bg"
    >
      {iso2 && (
        <span className="shrink-0 flex items-center opacity-80">
          <Flag code={iso2} width={15} height={15} circle={true} />
        </span>
      )}
      <span className="flex-1 truncate text-sm 3xl:text-base">{label}</span>
      <ArrowUpRight className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-25" />
    </Link>
  )
}

function Section({
  title,
  links,
  loading,
  columns = 1,
}: {
  title: string
  links: LinkItem[]
  loading?: boolean
  columns?: 1 | 2 | 3 | 4
}) {
  const colClass =
    columns === 4
      ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
      : columns === 3
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        : columns === 2
          ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-1'

  return (
    <div>
      <p className="mb-3 text-sm 3xl:text-base font-bold uppercase tracking-[0.25em] text-brand-bg">
        {title}
      </p>
      {loading ? (
        <div className={`grid ${colClass} gap-x-2 gap-y-1`}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-8 global-border-radius bg-brand-bg/[0.06] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className={`grid ${colClass} gap-x-2 gap-y-0.5 `}>
          {links.map((link) => (
            <SitemapLink key={link.href} {...link} />
          ))}
        </div>
      )}
    </div>
  )
}

const POPULAR_COUNTRIES: LinkItem[] = [
  { label: 'Indonesia eSIM', href: '/travel-esim/indonesia', iso2: 'id' },
  { label: 'Japan eSIM', href: '/travel-esim/japan', iso2: 'jp' },
  { label: 'Thailand eSIM', href: '/travel-esim/thailand', iso2: 'th' },
  { label: 'Singapore eSIM', href: '/travel-esim/singapore', iso2: 'sg' },
  { label: 'Malaysia eSIM', href: '/travel-esim/malaysia', iso2: 'my' },
  { label: 'United States eSIM', href: '/travel-esim/united-states', iso2: 'us' },
  { label: 'United Kingdom eSIM', href: '/travel-esim/united-kingdom', iso2: 'gb' },
  { label: 'Australia eSIM', href: '/travel-esim/australia', iso2: 'au' },
  { label: 'New Zealand eSIM', href: '/travel-esim/new-zealand', iso2: 'nz' },
  { label: 'France eSIM', href: '/travel-esim/france', iso2: 'fr' },
  { label: 'Germany eSIM', href: '/travel-esim/germany', iso2: 'de' },
  { label: 'Italy eSIM', href: '/travel-esim/italy', iso2: 'it' },
  { label: 'Spain eSIM', href: '/travel-esim/spain', iso2: 'es' },
  { label: 'South Korea eSIM', href: '/travel-esim/south-korea', iso2: 'kr' },
  { label: 'Canada eSIM', href: '/travel-esim/canada', iso2: 'ca' },
  { label: 'Vietnam eSIM', href: '/travel-esim/vietnam', iso2: 'vn' },
  { label: 'Philippines eSIM', href: '/travel-esim/philippines', iso2: 'ph' },
  { label: 'India eSIM', href: '/travel-esim/india', iso2: 'in' },
  { label: 'Cambodia eSIM', href: '/travel-esim/cambodia', iso2: 'kh' },
  { label: 'Taiwan eSIM', href: '/travel-esim/taiwan', iso2: 'tw' },
]

export default function SitemapPage() {
  const { data: regionsData, isLoading: regionsLoading } = usePublicRegions()

  const regionLinks: LinkItem[] = (regionsData?.data ?? []).map((r) => ({
    label: `${r.region} eSIM`,
    href: `/travel-esim/${toEsimSlug(r.region)}`,
  }))

  return (
    <div className="min-h-screen bg-brand-text">
      {/* Hero */}
      <div className="border-b border-brand-bg/10 bg-white py-16">
        <div className="mx-auto px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-brand-bg">
            Navigation
          </p>
          <h1 className="text-4xl sm:text-6xl font-bold text-brand-bg tracking-tight">
            Sitemap
          </h1>
          <p className="mt-3 text-sm text-brand-bg/50 max-w-md">
            Everything Vell offers — money transfers, eSIMs, tools, and more.
          </p>
        </div>
      </div>

      <div className="mx-auto px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] py-16 space-y-16">

        {/* Primary sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          <Section
            title="Personal Banking"
            links={[
              { label: 'Personal Account', href: '/personal-account' },
              { label: 'Create Account', href: '/register' },
              // { label: 'Currency Converter', href: '/resources/currency-converter' },
              // { label: 'Historical Exchange Rates', href: '/resources/historical-rates' },
              { label: 'Rate Tracker', href: '/resources/rateTracker' },
              { label: 'Swift Code Finder', href: '/resources/swift-code-finder' },
              { label: 'Money Transfer Comparison', href: '/resources/international-money-transfer-comparison' },
            ]}
          />
          <Section
            title="Business"
            links={[
              { label: 'Business Account', href: '/business-account' },
            ]}
          />
          <Section
            title="Travel eSIM"
            links={[
              { label: 'Browse eSIM Plans', href: '/travel-esim' },
              { label: 'Compare Providers', href: '/travel-esim-comparison' },
              { label: 'Installation Guide', href: '/travel-esim/install-esim' },
              { label: 'Activation Guide', href: '/travel-esim/activate-esim' },
              { label: 'eSIM Terms & Conditions', href: '/travel-esim/terms-and-conditions' },
              // { label: 'eSIM Guidelines', href: '/esim-guideline' },
            ]}
          />
          <Section
            title="Company"
            links={[
              { label: 'About Vell', href: '/about-vell' },
              { label: 'Our Company', href: '/our-company' },
              // { label: 'Sustainability', href: '/sustainability' },
              { label: 'Careers', href: '/career' },
              { label: 'Contact Us', href: '/contact-us' },
              { label: 'Download App', href: '/download-app' },
            ]}
          />
          <Section
            title="Help & Support"
            links={[
              { label: 'Frequently Asked Questions', href: '/frequently-asked-questions' },
              { label: 'About Vell — FAQ', href: '/frequently-asked-questions/about-vell' },
              { label: 'Pricing & Fees', href: '/frequently-asked-questions/pricing-and-fees' },
              { label: 'Sending Money', href: '/frequently-asked-questions/sending-money' },
              { label: 'Transfer Time & Tracking', href: '/frequently-asked-questions/transfer-time-and-tracking' },
              { label: 'Scheduled Payments', href: '/frequently-asked-questions/scheduled-payments' },
              { label: 'Security & Protection', href: '/security-and-protection' },
            ]}
          />
          <Section
            title="Legal"
            links={[
              { label: 'Privacy Policy', href: '/legal/privacy-policy' },
              { label: 'Terms & Conditions', href: '/legal/terms-and-conditions' },
              { label: 'Cookie Policy', href: '/legal/cookie-policy' },
              { label: 'Complaints Policy', href: '/legal/complaints-policy' },
              { label: 'Promotional Offer Terms', href: '/legal/promotional-offer-terms-and-conditions' },
            ]}
          />
        </div>

        <div className="border-t border-brand-bg/10" />

        {/* eSIM Destinations */}
        <Section
          title="Popular eSIM Destinations"
          links={POPULAR_COUNTRIES}
          columns={4}
        />

        <Section
          title="eSIM Regions"
          links={regionLinks}
          loading={regionsLoading}
          columns={4}
        />

        <div className="border-t border-brand-bg/10" />

        {/* Currency tools */}
        <Section
          title="Send Money from Australia"
          links={SEND_MONEY_CORRIDORS}
          columns={4}
        />

        {/* <Section
          title="Historical Exchange Rates"
          links={historicalRatesLinks}
          loading={currenciesLoading}
          columns={4}
        /> */}

      </div>
    </div>
  )
}
